/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import { useI18n } from "hooks/i18n/i18n";
import { useState } from "react";
import { useSelector } from "store";

import { LiveThumbnailPreview, VODThumbnailPreview } from "./ThumbnailPreview";
import { getValidUrl } from "./getValidUrl";
import { generatePlaceholderImage } from "./placeholderImage";
import { useVideoGroupsSet } from "./useVideoGroupsSet";

import type Player from "video.js/dist/types/player";

const closedContainerStyle = css({
  position: "absolute",
  bottom: "92px",
  right: "8px",
  zIndex: 999,
  borderRadius: "12px",
  background: "rgba(0, 0, 0, 0.30)",
  color: "white",
  opacity: 1,
  transition: "opacity 1.0s",
  ".vjs-layout-small &, .vjs-layout-x-small &": {
    bottom: "initial",
    top: "12px",
    right: "16px",
    padding: "4px",
    background: "transparent",
  },
  ".vjs-user-inactive:not(.vjs-paused) &": {
    opacity: 0,
  },
});

const openButtonStyle = css({
  "&&": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    borderRadius: "12px",
    ".vjs-layout-small &, .vjs-layout-x-small &": {
      padding: "0px",
    },
  },
});

const openButtonTextStyle = css({
  fontSize: 14,
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "20px",
  ".vjs-layout-small &, .vjs-layout-x-small &": {
    display: "none",
  },
});

const openButtonThumbnailStyle = css({
  boxSizing: "content-box",
  borderRadius: "8px",
  border: "3px solid #fff",
  width: "60px",
  height: "34px",
  ".vjs-layout-small &, .vjs-layout-x-small &": {
    width: "40px",
    height: "23px",
    border: "2px solid #fff",
  },
});

const openButtonThumbnailContainerStyle = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const openButtonThumbnailTopBarStyle = css({
  width: "20px",
  height: "4px",
  borderRadius: "16px",
  background: "rgba(255, 255, 255, 0.50)",
  marginBottom: "2px",
  ".vjs-layout-small &, .vjs-layout-x-small &": {
    width: "13.333px",
    height: "2.667px",
  },
});

const openButtonThumbnailBottomBarStyle = css({
  width: "40px",
  height: "4px",
  borderRadius: "4px 4px 0 0",
  background: "#fff",
  marginBottom: "3px",
  ".vjs-layout-small &, .vjs-layout-x-small &": {
    width: "26.666px",
    height: "2.667px",
  },
});

const openContainerStyle = css({
  position: "absolute",
  paddingTop: "12px",
  paddingLeft: "16px",
  paddingRight: "16px",
  paddingBottom: "8px",
  bottom: "84px",
  left: "8px",
  zIndex: 9999,
  color: "black",
  width: "calc(100% - 16px)",
  borderRadius: "8px",
  // RTX VSR が非アクティブになるため、backdrop-filter は使用しない。できるだけ見た目を近づけている
  backgroundColor: "rgba(0, 0, 0, 0.55)",
  backgroundImage:
    "linear-gradient(to bottom, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0) 35%, rgba(0, 0, 0, 0.1))",
  border: "1px solid rgba(255, 255, 255, 0.07)",
  boxShadow:
    "0 10px 24px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.08)",
  ".vjs-layout-small &, .vjs-layout-x-small &": {
    bottom: "0px",
    width: "100%",
    left: "0px",
    borderRadius: "0px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
});

const upperShadowOverlay = css({
  ".vjs-layout-small &, .vjs-layout-x-small &": {
    position: "absolute",
    top: "0px",
    right: "0px",
    width: "100%",
    height: "100px",
    background:
      "linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, rgba(255, 255, 255, 0) 100%)",
  },
});

const headerStyle = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "8px",
});

const headerLeftStyle = css({
  display: "flex",
  alignItems: "center",
  gap: "16px",
});

const titleStyle = css({
  color: "white",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "20px",
});

// 通常レイアウト時のみ表示（small/x-smallでは非表示）
const tabContainerForLargeStyle = css({
  display: "flex",
  gap: "8px",
  alignItems: "center",
  ".vjs-layout-small &, .vjs-layout-x-small &": {
    display: "none",
  },
});

// small/x-smallレイアウト時のみ表示（通常では非表示）
const tabContainerForSmallStyle = css({
  display: "none",
  gap: "8px",
  alignItems: "center",
  ".vjs-layout-small &, .vjs-layout-x-small &": {
    display: "flex",
  },
  marginBottom: "12px",
});

const tabButtonStyle = (isSelected: boolean) =>
  css({
    "&&": {
      fontSize: "12px",
      fontWeight: "bold",
      lineHeight: "16px",
      padding: "4px 8px",
      borderRadius: "9999px",
      color: isSelected ? "black" : "white",
      backgroundColor: isSelected ? "white" : "#fff3",
      border: "none",
      cursor: "pointer",
    },
  });

const videoListStyle = css({
  display: "flex",
  gap: "16px",
  overflowX: "auto",
});

const CloseButton = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M15 5L5 15"
        stroke="white"
        strokeOpacity="0.7"
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 5L15 15"
        stroke="white"
        strokeOpacity="0.7"
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

type MultiAngleSwitcherContainerProps = {
  player: Player;
  handleSwitchMultiAngleVideo: (videoId: string) => void;
};

export const MultiAngleSwitcherContainer: React.FC<
  MultiAngleSwitcherContainerProps
> = ({ player, handleSwitchMultiAngleVideo }) => {
  const streamingKey = useSelector((state) => state.streaming.streamingKey);
  const streamingType = useSelector((state) => state.streaming.streamingType);
  const isVOD = streamingType === "VOD";

  const currentVideoId = useSelector((state) => state.streaming.currentVideoId);

  const [isMultiAngleViewOpen, setIsMultiAngleViewOpen] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const event = useSelector((state) => state.event);
  const { t } = useI18n();

  const videoGroupsSet = useVideoGroupsSet(currentVideoId);

  if (!currentVideoId) {
    return null;
  }

  if (videoGroupsSet === undefined) {
    return null;
  }

  if (!isMultiAngleViewOpen) {
    return (
      <div>
        <div css={upperShadowOverlay} />
        <div css={closedContainerStyle}>
          <button
            css={openButtonStyle}
            onClick={() => {
              setIsMultiAngleViewOpen(true);
            }}
            type="button"
          >
            <span css={openButtonTextStyle}>
              {t("streaming.multiAngle.cameraSwitch")}
            </span>
            <div css={openButtonThumbnailContainerStyle}>
              <div css={openButtonThumbnailTopBarStyle} />
              <div css={openButtonThumbnailBottomBarStyle} />
              <img
                src={
                  getValidUrl(videoGroupsSet.mainVideo?.thumbnailUrl) ??
                  getValidUrl(event.displayEvent.thumbnail) ??
                  generatePlaceholderImage(
                    t("streaming.multiAngle.cameraSwitch")
                  )
                }
                alt="Camera Switch Icon"
                css={openButtonThumbnailStyle}
              />
            </div>
          </button>
        </div>
      </div>
    );
  }

  const VideoGroupTab = ({
    containerStyle,
  }: {
    containerStyle: ReturnType<typeof css>;
  }) => {
    return (
      <div css={containerStyle}>
        {videoGroupsSet.videoGroups.map((group, index) => (
          <button
            key={group.name}
            onClick={() => setSelectedTabIndex(index)}
            css={tabButtonStyle(selectedTabIndex === index)}
          >
            {group.name}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div css={openContainerStyle}>
      <div css={headerStyle}>
        <div css={headerLeftStyle}>
          <h3 css={titleStyle}>{t("streaming.multiAngle.cameraSwitch")}</h3>
          <VideoGroupTab containerStyle={tabContainerForLargeStyle} />
        </div>
        <button onClick={() => setIsMultiAngleViewOpen(false)}>
          <CloseButton />
        </button>
      </div>

      <div>
        <VideoGroupTab containerStyle={tabContainerForSmallStyle} />
        <div css={videoListStyle}>
          {(() => {
            /**
             * selectedTabIndex が範囲外になっている場合に備えてクランプする
             * 1日目, 2日目の切り替えなどで videoGroupsSet が変わる場合に対応
             */
            const clampSelectedTabIndex = Math.max(
              Math.min(selectedTabIndex, videoGroupsSet.videoGroups.length - 1),
              0
            );

            if (clampSelectedTabIndex !== selectedTabIndex) {
              setSelectedTabIndex(clampSelectedTabIndex);
            }

            return videoGroupsSet.videoGroups[
              clampSelectedTabIndex
            ]?.videos.map((video) => {
              const cookie = streamingKey?.cookies?.[video.videoId]?.default;

              if (cookie === undefined) {
                return null;
              }

              const isCurrentVideo = video.videoId === currentVideoId;

              if (isVOD) {
                return (
                  <VODThumbnailPreview
                    key={video.videoId}
                    video={video}
                    cookie={cookie}
                    isCurrentVideo={isCurrentVideo}
                    player={player}
                    handleSwitchMultiAngleVideo={handleSwitchMultiAngleVideo}
                    eventThumbnailUrl={event.displayEvent.thumbnail}
                  />
                );
              }

              return (
                <LiveThumbnailPreview
                  key={video.videoId}
                  video={video}
                  cookie={cookie}
                  isCurrentVideo={isCurrentVideo}
                  player={player}
                  handleSwitchMultiAngleVideo={handleSwitchMultiAngleVideo}
                  eventThumbnailUrl={event.displayEvent.thumbnail}
                />
              );
            });
          })()}
        </div>
      </div>
    </div>
  );
};
